import { useMemo } from 'react';

import { Cluster } from '@cast/types';

import {
  isOverviewQueryUnresolved,
  useOrganizationOverviewQuery,
} from '@cast/shared-cost-data-access';

import { useSearchContextClusters } from '@cast/features-cluster-filters';
import {
  getClustersOverviewRangeIso,
  useClustersOverviewDateRange,
} from '@cast/features-cluster-shared';
import {
  deriveEstimatedSavingsMapFromOverview,
  deriveSloHeatmapFromOverview,
  deriveSummaryMapFromOverview,
  mapOverviewSloToColumnData,
  type SloMetric,
} from '@cast/features-cluster-shared-utils';

import {
  buildClusterHeatmapData,
  isExcludedFromHeatmap,
  type HeatmapViewMode,
} from '../utils/clusterHeatmap';

type UseClusterHeatmapDataArgs = {
  mode: HeatmapViewMode;
};

export const useClusterHeatmapData = ({ mode }: UseClusterHeatmapDataArgs) => {
  const { clusters } = useSearchContextClusters<Cluster>();
  const range = useClustersOverviewDateRange();
  const { startTime, endTime } = getClustersOverviewRangeIso(range);
  const {
    data: overview,
    isFetching,
    error,
    refetch,
  } = useOrganizationOverviewQuery({ startTime, endTime });
  const overviewErrorForUi = isFetching ? undefined : error;
  const isLoading = isOverviewQueryUnresolved(overview, overviewErrorForUi);

  const summaryMap = useMemo(
    () => deriveSummaryMapFromOverview(overview?.clusters),
    [overview?.clusters]
  );
  const estimatedSavingsMap = useMemo(
    () => deriveEstimatedSavingsMapFromOverview(overview?.clusters),
    [overview?.clusters]
  );

  const sloMap = useMemo(() => {
    const map = new Map<
      string,
      ReturnType<typeof deriveSloHeatmapFromOverview>
    >();

    overview?.clusters?.forEach((item) => {
      if (item.clusterId) {
        map.set(item.clusterId, deriveSloHeatmapFromOverview(item.slo));
      }
    });

    return map;
  }, [overview?.clusters]);

  const reliabilityMetricsMap = useMemo(() => {
    const map = new Map<string, SloMetric[]>();

    overview?.clusters?.forEach((item) => {
      if (item.clusterId) {
        map.set(item.clusterId, mapOverviewSloToColumnData(item.slo).metrics);
      }
    });

    return map;
  }, [overview?.clusters]);

  const heatmapData = useMemo(
    () =>
      buildClusterHeatmapData({
        clusters,
        estimatedSavingsMap,
        fallbackSummaryMap: new Map(),
        summaryMap,
        mode,
        sloMap,
        reliabilityMetricsMap,
      }),
    [
      clusters,
      estimatedSavingsMap,
      mode,
      reliabilityMetricsMap,
      sloMap,
      summaryMap,
    ]
  );

  const hasClustersWithoutHeatmap = useMemo(
    () =>
      clusters.some((cluster) => isExcludedFromHeatmap(cluster.displayStatus)),
    [clusters]
  );

  return {
    clusters,
    heatmapData,
    hasClustersWithoutHeatmap,
    isLoading,
    error: overviewErrorForUi,
    refetch,
  };
};
